import { getAnalyticsTrackingId, getIpData } from '@monoimin/utils/analytics';
import { IconNamesUnion, Locales, FormInputs } from '@monoimin/sb-types';
import {
  checkRule,
  generateId,
  getCookie,
  getUrlOrigin,
} from '@monoimin/utils/helpers';
import { RENTA_WRITE_KEY } from '@monoimin/constants';

import { CountryCode } from 'libs/utils/src/lib/redis';

import {
  FieldConfig,
  FormValues,
  FormValuesById,
  InputConditionalLogic,
  DataForCollection,
  SubscribeFormProps,
  FormRequestData,
  ValidateEmailSuccessResponse,
  FormZbFields,
  FormLogEvent,
} from '../types';

const invalidStatuses = ['invalid', 'spamtrap', 'abuse', 'do_not_mail'];
export const getEmailValidationData = async (
  email?: FormDataEntryValue
): Promise<FormZbFields | null> => {
  if (!email) {
    return null;
  }

  const validateEmailRes: ValidateEmailSuccessResponse | null = await fetch(
    '/api/validate-email/',
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    }
  )
    .then(onFetchSuccess)
    .catch(onFetchFailure);

  const isValidEmail =
    validateEmailRes && !invalidStatuses.includes(validateEmailRes.status);

  if (!isValidEmail) {
    return null;
  }

  return {
    zb_domain: validateEmailRes?.domain || '',
    zb_free_email: validateEmailRes?.free_email,
    zb_smtp_provider: validateEmailRes?.smtp_provider || '',
    zb_account: validateEmailRes?.account || '',
    zb_status: validateEmailRes?.status || '',
    zb_sub_status: validateEmailRes?.sub_status || '',
  };
};

export const logFormEvent = async ({
  userAction,
  locale,
  rentaResponse,
  formTitle,
  formId,
  filteredFormValues,
  formLogData,
  message,
}: {
  userAction: FormLogEvent;
  locale: Locales;
  formId: string;
  rentaResponse?: string;
  formTitle: string;
  filteredFormValues?: Record<string, string>;
  formLogData?: Record<string, any>;
  message?: string;
}) => {
  const timestamp = Date.now() * 1000;

  const origin = getUrlOrigin();

  const trackingId = getAnalyticsTrackingId();

  const pureHref =
    typeof window !== 'undefined' ? origin + window.location.pathname : origin;

  const metaAttributes = {
    ga: trackingId,
    language_form: locale,
    fr_form_id: formId,
    hostname: origin,
    domain: origin,
    fr_form_title: formTitle,
    fr_source_url: pureHref,
    path: window.location.pathname,
    form_action: userAction,
    form_values: filteredFormValues,
    form_data: formLogData,
  };

  const logMessage = message ?? 'Gravity forms logs';
  const data = rentaResponse
    ? {
        attributes: metaAttributes,
        timestamp,
        message: logMessage,
        'RENTA response': rentaResponse,
      }
    : { attributes: metaAttributes, timestamp, message: logMessage };

  return fetch('/api/log/', {
    method: 'POST',
    body: JSON.stringify(data),
  })
    .then(onFetchSuccess)
    .catch(onFetchFailure);
};

export const getFieldCustomId = ({
  type,
  id,
  formId,
}: {
  type: string;
  id: number;
  formId: number;
}) => `form` + formId + type + id;

export const getInputName = (field: FieldConfig) => {
  const labelName = field.type === 'hidden' ? field.label : field.adminLabel;

  return labelName || getFieldCustomId(field);
};

export const showByAllConditionalLogic = ({
  conditionalLogic,
  formValuesById,
}: {
  conditionalLogic: InputConditionalLogic | string;
  formValuesById: FormValuesById;
}): boolean => {
  if (!conditionalLogic || typeof conditionalLogic === 'string') {
    return true;
  }

  const { rules, enabled, logicType } = conditionalLogic ?? {};

  if (!enabled || !rules?.length) return true;

  if (logicType === 'all') {
    return rules.every((rule) => checkRule(rule, formValuesById));
  }

  if (logicType === 'any') {
    return rules.some((rule) => checkRule(rule, formValuesById));
  }

  return false;
};

export function getButtonIconAndText(inputString: string): {
  text: string;
  iconName: IconNamesUnion | undefined;
} {
  if (!inputString) {
    return { iconName: undefined, text: inputString };
  }
  const regex = /\$\$(\w+)/;
  const match = String(inputString)?.match(regex);

  if (!match) {
    return { iconName: undefined, text: inputString };
  }

  const word = match[1];
  // const position = match.index <= inputString.length / 2 ? 'start' : 'end';
  const updatedString = inputString.replace(match[0], '').trim();
  return { iconName: word as IconNamesUnion, text: updatedString };
}

export const onFetchFailure = () => {
  return null;
};

export const onFetchSuccess = (res: Response) => {
  if (res.ok) {
    return res.json();
  }

  return null;
};

const isCountryToChangeLang = async (
  countryCode: string,
  phoneNumber?: string
) => {
  const response = await fetch('/api/country-codes');
  const countriesToChangeLang = await response.json();

  if (!countriesToChangeLang?.length) {
    return false;
  }

  return countriesToChangeLang.some(
    (country: CountryCode) =>
      country.code === countryCode || phoneNumber?.startsWith(country.phoneCode)
  );
};

// ключ – это код записи в SalesForce на русском, значение – код записи на английском
const ruToEnSfRecordTypeCodes: Record<string, string> = {
  '0124H000000kMjPQAU': '012580000006MylAAE', // fs
  '0124H000000kMjOQAU': '0124I0000006TSMQA2', // price
  '0124H000000kMjQQAU': '012580000005hxwAAA', // mtg / Back call
  '0124H000000kMjLQAU': '012580000005hpRAAQ', // question
  '0124H000000kMjCQAU': '01258000000wntkAAA', // partner
  '0124H000000kMjGQAU': '012580000005hteAAA', // press center
  '0124H000000kMjEQAU': '0124I000000Fa7VQAS', // survey
  '0124H000000kMjNQAU': '012580000005hy1AAA', // real estate inquiry
};
// если юзер зашел на ру форму, но при этом его номер/айпи адрес из страны, которая находится в списке countriesToChangeLang, то нужно отправлять данные на английский SalesForce
export const getActualSfConfig = async (
  locale: Locales,
  sfRecordTypeCode: string,
  countryCode: string,
  phoneNumber?: string
) => {
  if (
    sfRecordTypeCode &&
    (await isCountryToChangeLang(countryCode, phoneNumber))
  ) {
    sfRecordTypeCode =
      ruToEnSfRecordTypeCodes[sfRecordTypeCode] || sfRecordTypeCode;
    locale = 'en';
  }
  return { sfRecordTypeCode, locale };
};

export const onValidateEmailFailure = (
  result: ValidateEmailSuccessResponse | null,
  formName?: string,
  formId?: SubscribeFormProps['id']
) => {
  if (!result) {
    return;
  }

  if ('dataLayer' in window) {
    const trackingId = getAnalyticsTrackingId();

    window.dataLayer.push({
      event: 'Invalid email',
      inval_form_name: formName ?? '',
      inval_form_id: formId ?? '',
      inval_ga: trackingId as string,
      invalid_email: result.address,
      ZB_status: result.status,
      ZB_sub_status: result.sub_status,
      ZB_domain: result.domain,
      ZB_account: result.account,
      ZB_free_email: result.free_email,
      ZB_smtp_provider: result.smtp_provider,
    });
  }
};

export const generateCollectionDataPayload = async (
  values: FormValues | FormInputs,
  title: string,
  locale: Locales,
  validateEmailData: FormZbFields | null,
  pureHref: string,
  trackingId: string,
  date: string,
  timezone: string,
  timezoneOffset: string
): Promise<FormRequestData> => {
  const requestId = generateId(32);

  const platform = getCookie('platform') || '';
  const ipData = await getIpData();

  const pageLocale = locale;

  const userPhone = values['user_phone'] ? String(values['user_phone']) : '';

  let sfRecordTypeCode = values['sf_record_type_code']
    ? String(values['sf_record_type_code'])
    : '';

  // Filter out keys that include 'pr_children_' and have value '0'
  const filteredValues = Object.fromEntries(
    Object.entries(values).filter(
      ([key, value]) => !(key.includes('pr_children_') && value === '0')
    )
  );

  let updatedLocale = locale;
  if (sfRecordTypeCode) {
    const config = await getActualSfConfig(
      locale,
      sfRecordTypeCode,
      ipData.ipCountryCode || '',
      userPhone
    );

    sfRecordTypeCode = config.sfRecordTypeCode;
    updatedLocale = config.locale;
  }

  const submitData = {
    ...filteredValues,
    ...(validateEmailData || {}),
    ...ipData,
    sf_record_type_code: sfRecordTypeCode,
    user_Lastname: !filteredValues['user_Lastname']
      ? '-'
      : String(filteredValues['user_Lastname']),
    fr_form_title: title,
    fr_date_created: date,
    ga_cookie_id: trackingId,
    device_type: getCookie('dtype') || '-',
    fr_source_url: pureHref,
    user_agent: navigator.userAgent,
    language_form: pageLocale,
    filter_locale: updatedLocale,
    requestId: requestId,
    platform,
    sf_status: 'New',
    sf_currency_iso_code: 'USD',
    domain: window.location.hostname,
    timezone,
    sf_timezone: timezoneOffset,
  };

  return {
    ...submitData,
  };
};

export const createCollectionData = async (
  pureHref: string,
  rid: string,
  trackingId: string,
  collectionDataPayload: FormRequestData,
  date: string,
  adsIds: Record<string, string | null>
): Promise<DataForCollection> => {
  const fbpCookieId = getCookie('_fbp');

  const { localStorage, cookies } = isStorageAccessible();

  return {
    timestamp: date,
    context: {
      ads: adsIds,
      page: {
        path: window.location.pathname,
        referrer: document.referrer,
        title: document.title,
        url: pureHref,
        location: pureHref,
        hostname: origin,
      },
      device: {
        deviceDocEncoding: 'UTF-8',
        userAgent: navigator.userAgent,
        deviceScreenWidth: window.screen.width,
        deviceScreenHeight: window.screen.height,
        deviceViewPortWidth: window.innerWidth,
        deviceViewPortHeight: window.innerHeight,
      },
      cookies: {
        ga: trackingId,
        ym: null,
        fbp: fbpCookieId ?? null,
        fbc: null,
        localStorageAccessible: localStorage,
        cookiesAccessible: cookies,
      },
      library: {
        name: 'renta.js',
        version: '2.0',
      },
      protocols: {
        environment: 'prod',
        eventVersion: 1.1,
      },
    },
    properties: collectionDataPayload,
    name: 'Form submitted',
    messageId: generateId(32),
    anonymousId: rid,
    type: 'track',
    writeKey: RENTA_WRITE_KEY || '',
    userId: null,
    sentAt: date,
  };
};

export const isStorageAccessible = (): {
  localStorage: boolean;
  cookies: boolean;
} => {
  const testKey = '__test__';
  let isLocalStorageAccessible = false;
  let isCookiesAccessible = false;

  // Проверка, что localStorage доступен
  try {
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    isLocalStorageAccessible = true;
  } catch (e) {
    if (e instanceof DOMException) {
      console.warn('localStorage not accessible:', e.message);
    } else {
      throw e;
    }
  }

  // Проверка, что cookies доступны
  try {
    if (typeof document !== 'undefined') {
      document.cookie = `${testKey}=test; path=/`;
      isCookiesAccessible = document.cookie.includes(`${testKey}=test`);
      document.cookie = `${testKey}=; Max-Age=-99999999; path=/`;
    }
  } catch (e) {
    console.warn(
      'Cookie check failed:',
      e instanceof Error ? e.message : 'Unknown error'
    );
  }

  return {
    localStorage: isLocalStorageAccessible,
    cookies: isCookiesAccessible,
  };
};
